import { useParams, useNavigate } from 'react-router-dom';
import { Fragment, useEffect, useState } from 'react';

import BusinessTwoToneIcon from '@mui/icons-material/BusinessTwoTone';
import PestControlRodentTwoToneIcon from '@mui/icons-material/PestControlRodentTwoTone';
import ElectricalServicesTwoToneIcon from '@mui/icons-material/ElectricalServicesTwoTone';
import CloudTwoToneIcon from '@mui/icons-material/CloudTwoTone';
import StorageTwoToneIcon from '@mui/icons-material/StorageTwoTone';
import VpnLockTwoToneIcon from '@mui/icons-material/VpnLockTwoTone';
import CheckCircleOutlineTwoToneIcon from '@mui/icons-material/CheckCircleOutlineTwoTone';
import ErrorTwoToneIcon from '@mui/icons-material/ErrorTwoTone';
import AutoFixHighTwoToneIcon from '@mui/icons-material/AutoFixHighTwoTone';
import AnnouncementTwoToneIcon from '@mui/icons-material/AnnouncementTwoTone';

import { Timeline, TimelineConnector, TimelineContent, TimelineDot, TimelineItem, TimelineSeparator } from '@mui/lab';

import { Wrapper } from './ClientPage.style';
import { getEmptyTestResult, ISuggestion, ITestResult, ITestResultInfo, ITestStep, ITestStepResult, SuggestionType } from '../../models/test-steps.model';
import { Card, CardHeader, CircularProgress, Grid, List, ListItem, ListItemIcon, Typography, ListItemText } from '@mui/material';
import { getCompanyByCompanyUid, getCompanyDatabasesByUid } from '../../api/portal.api';
import { getDmsInfo, getDmsVersion } from '../../api/dms.api';
import { IDatabaseDetail } from '../../models/portal.model';
import { IDmsInfo, IDmsInfoBlock } from '../../models/dms.model';
import axios, { AxiosError } from 'axios';

interface IComponentProps {}

const ClientPage: React.FC<IComponentProps> = function ({}) {
  const [steps, setSteps] = useState<ITestStep[]>([]);
  const params = useParams();
  const [companyUid, setCompanyUid] = useState('');
  const [currentStepIndex, setCurrentStepIndex] = useState(-1);
  const [results, setResults] = useState<ITestResult[]>([]);
  const navigate = useNavigate();

  // Local store
  let _databases = [] as IDatabaseDetail[];

  useEffect(() => {
    try {
      setCompanyUid(params.id || 'UNKNOWN');

      if (!params.id) {
        navigate('/');
      }
    } catch (ex) {
      navigate('/');
    }
  });

  useEffect(() => {
    if (steps.length > 0) {
      setCurrentStepIndex(0);
    }
  }, [steps.length]);

  useEffect(() => {
    const processStep = async () => {
      const step = steps[currentStepIndex];
      let hasFailed = false;
      let hasCompleted = false;
      let result = {} as ITestStepResult;

      try {
        setSteps((prev) => {
          let newSteps = [...prev];
          newSteps.splice(currentStepIndex, 1, {
            ...step,
            isProcessing: true,
          });
          return newSteps;
        });

        if (step.function) {
          result = { ...(await step.function()) };
        } else {
          result = {
            description: '',
          };
        }
      } catch (ex) {
        hasFailed = true;
      } finally {
        hasCompleted = true;

        setSteps((prev) => {
          let newSteps = [...prev];
          newSteps.splice(currentStepIndex, 1, {
            ...step,
            hasFailed,
            hasCompleted,
            isProcessing: false,
            result: result,
          });
          return newSteps;
        });

        if (!hasFailed) {
          setCurrentStepIndex((prev) => {
            return prev + 1;
          });
        }
      }
    };

    if (currentStepIndex >= 0 && currentStepIndex < steps.length) {
      processStep();
    }
  }, [currentStepIndex]);

  const getSteps = (companyUid: string) => {
    return [
      {
        order: 0,
        title: 'Get company info',
        description: `Retrieving information for company uid ${companyUid}`,
        icon: BusinessTwoToneIcon,
        result: {} as ITestStepResult,
        function: fetchCompanyByCompanyUid,
      } as ITestStep,
      {
        order: 2,
        title: 'Get databases',
        description: 'Retrieving registered client databases',
        icon: StorageTwoToneIcon,
        result: {} as ITestStepResult,
        function: fetchDatabasesByCompanyUid,
      } as ITestStep,
      {
        order: 3,
        title: 'Test access to client gateway',
        description: 'Connect to client gateway via RAT',
        icon: CloudTwoToneIcon,
        result: {} as ITestStepResult,
      } as ITestStep,
      {
        order: 4,
        title: '',
        description: '',
        icon: PestControlRodentTwoToneIcon,
        result: {} as ITestStepResult,
      } as ITestStep,
      {
        order: 5,
        title: 'Connect to client',
        description: 'Connect to client gateway via RAT',
        icon: VpnLockTwoToneIcon,
        result: {} as ITestStepResult,
        function: fetchDmsInfo,
      } as ITestStep,
    ] as ITestStep[];
  };

  useEffect(() => {
    if (companyUid) {
      setSteps(getSteps(companyUid));
    }
  }, [companyUid]);

  const addResult = (newResult: ITestResult, isError?: boolean) => {
    setResults((prev) => {
      const res = [
        ...prev,
        {
          ...getEmptyTestResult(),
          ...newResult,
          isError: isError ? true : false,
        } as ITestResult,
      ];
      return res;
    });
  };

  const fetchCompanyByCompanyUid = async () => {
    try {
      const companyResults = await getCompanyByCompanyUid(companyUid);
      addResult({
        description: `Company ${companyResults.gatewayDescription} found with id ${companyUid}`,
      } as ITestResult);
      return {
        description: companyResults.gatewayDescription,
      } as ITestStepResult;
    } catch (ex) {
      console.error(`Failed to process ${fetchCompanyByCompanyUid.name}. ${ex}`);
      addResult(
        {
          description: `Failed to get company by id ${companyUid}`,
        } as ITestResult,
        true,
      );
      throw ex;
    }
  };

  const fetchDatabasesByCompanyUid = async () => {
    try {
      const companyUid = params.id || 'UNKNOWN';
      const databaseResults = await getCompanyDatabasesByUid(companyUid);
      _databases = [...databaseResults];
      addResult({
        description: `Databases found for company uid ${companyUid}`,
        items: _databases.map((db) => db.dbDisplayName),
      } as ITestResult);
      return {
        description: `${_databases.length} DBs found. Using "${_databases[0].dbDisplayName}"`,
      } as ITestStepResult;
    } catch (ex) {
      console.error(`Failed to process ${fetchDatabasesByCompanyUid.name}. ${ex}`);
      addResult(
        {
          description: `Failed to get database by company uid ${companyUid}`,
        } as ITestResult,
        true,
      );
      throw ex;
    }
  };

  const fetchDmsInfo = async () => {
    try {
      const results = await getDmsInfo(_databases[0]?.dbId, companyUid, false);

      addResult({
        description: 'Successfully connected to DMS via WAN',
        items: [],
        info: [],
        isError: false,
      });

      if (results.lanConfig.hasError) {
        throw results.lanConfig;
      }

      const dmsInfoResultArray = getDmsInfoAsResultArray(results);
      addResult({
        description: `DMS info retrieved for database ${_databases[0].dbDisplayName} (${_databases[0]?.dbId})`,
        items: [],
        info: dmsInfoResultArray,
        isError: false,
      } as ITestResult);
      return { description: results.version.result } as ITestStepResult;
    } catch (dmsInfoEx: any) {
      let suggestions = [] as ISuggestion[];

      let dmsVersion = 'unknown';
      // Attempt to get dms version (if DmsInfo endpoint is unavailable due to old version of endpoints)
      try {
        const directVersion = (await getDmsVersion(_databases[0]?.dbId, companyUid)).data;
        dmsVersion = directVersion;
        suggestions.push({
          key: '',
          suggestion: `Managed to connect to the endpoints and retrieve the version`,
          type: SuggestionType.info,
        });
      } catch (dmsVersionEx: any) {
        suggestions.push({
          key: '',
          suggestion: `Failed to connect to the endpoints`,
          type: SuggestionType.error,
        });

        if (axios.isAxiosError(dmsVersionEx)) {
          const exception = dmsVersionEx as AxiosError;
          if (exception.response) {
            suggestions.push({
              key: '',
              suggestion: `Error ${exception.response.status}: ${exception.response.statusText}`,
              type: SuggestionType.error,
            });
            if (exception.response.data.Message && `${exception.response.data.Message}`.includes('SSL/TLS')) {
              suggestions.push({
                key: '',
                suggestion: `Please check the *.autoapps.cloud certificate to ensure it is installed and valid`,
                type: SuggestionType.instruction,
              });
            }
          }
        }

      }

      if (dmsVersion) {
        suggestions.push({
          key: '',
          suggestion: `Version ${dmsVersion} of the endpoints are installed`,
          type: SuggestionType.info,
        });

        // if (process.env.REACT_APP_RESOURCE_VERSION !== dmsVersion) {
        //   suggestions.push({ key: '', suggestion: `The version of installed endpoints (${dmsVersion}) is incorrect or out of date. The version of endpoints that should be installed is ${process.env.REACT_APP_RESOURCE_VERSION}`, type: SuggestionType.error });
        // }
      } else {
        suggestions.push({
          key: '',
          suggestion: `The version of the endpoints are unknown`,
          type: SuggestionType.error,
        });
      }

      if (dmsInfoEx.suggestions) {
        // Caught a thrown error from the lanConfig uyIDmsInfoBlock
        const lanConfig = dmsInfoEx as IDmsInfoBlock;
        const suggestions = lanConfig.suggestions?.map((s) => {
          return {
            suggestion: s,
            type: SuggestionType.instruction,
          } as ISuggestion;
        });
        addResult(
          {
            description: `Failed connecting to DMS via LAN`,
            info: [
              {
                hasError: true,
                errorMessage: lanConfig.errorMessage,
                result: lanConfig.result,
                stackTrace: lanConfig.stackTrace,
                suggestions: suggestions || [],
              },
            ],
          } as ITestResult,
          true,
        );
      } else {
        addResult(
          {
            description: `Failed to get DMS info for database ${_databases[0].dbDisplayName} (${_databases[0]?.dbId})`,
            info: [
              {
                hasError: true,
                errorMessage: 'Unable to communicate with the clients endpoints',
                result: '',
                stackTrace: '',
                suggestions: [...suggestions],
              },
            ],
          } as ITestResult,
          true,
        );
      }
      throw dmsInfoEx;
    }
  };

  const getDmsInfoAsResultArray = (dmsInfo: IDmsInfo): ITestResultInfo[] => {
    let res: ITestResultInfo[] = [];

    for (const [key, value] of Object.entries(dmsInfo)) {
      const valProp = { ...value } as IDmsInfoBlock;
      const valPropSuggestions = valProp.suggestions?.map((s) => {
        return {
          key,
          suggestion: s,
          type: SuggestionType.instruction,
        } as ISuggestion;
      });

      res.push({
        ...valProp,
        suggestions: valPropSuggestions,
      });
    }

    return res;
  };

  const getInfoItem = (result: string, type: SuggestionType, index: number, parentKey?: string) => {
    // Handle special cases
    if (parentKey === 'directoryConfig') {
      if (result.includes('and has correct permissions set')) {
        type = SuggestionType.success;
      } else {
        type = SuggestionType.error;
      }
    } else if (parentKey === 'documentReprint') {
      if (result.includes('Failed to')) {
        type = SuggestionType.error;
      }
    }

    switch (type) {
      case SuggestionType.error:
        return (
          <div className="result-item-icon-description" key={index}>
            <ErrorTwoToneIcon fontSize="small" color="error" />
            <ListItemText secondary={result} className="error-result" />
          </div>
        );
      case SuggestionType.info:
        return (
          <div className="result-item-icon-description" key={index}>
            <AnnouncementTwoToneIcon fontSize="small" color="info" />
            <ListItemText secondary={result} className="info-result" />
          </div>
        );
      case SuggestionType.instruction:
        return (
          <div className="result-item-icon-description" key={index}>
            <AutoFixHighTwoToneIcon fontSize="small" color="warning" />
            <ListItemText secondary={result} className="warning-result" />
          </div>
        );
      case SuggestionType.success:
        return (
          <div className="result-item-icon-description" key={index}>
            <AutoFixHighTwoToneIcon fontSize="small" color="success" />
            <ListItemText secondary={result} className="success-result" />
          </div>
        );
    }
  };

  return (
    <Wrapper>
      <Grid container spacing={2}>
        <Grid item xs="auto">
          <Timeline className="timeline">
            {steps.map((step, index) => {
              return (
                <TimelineItem className="timeline-item" key={step.order}>
                  <TimelineSeparator>
                    <TimelineDot className="timeline-dot" color={step.hasFailed ? 'error' : step.hasCompleted ? 'success' : 'info'}>
                      <CircularProgress value={step.hasCompleted ? 0 : undefined} variant={step.hasCompleted ? 'determinate' : 'indeterminate'} />
                      <step.icon className="step-icon" sx={{ width: 40, height: 40 }} />
                    </TimelineDot>
                    {index < steps.length - 1 && <TimelineConnector />}
                  </TimelineSeparator>
                  <TimelineContent sx={{ py: 0, px: 2, pb: 4, height: 100 }}>
                    <Typography variant="h6" component="span">
                      {step.title}
                    </Typography>
                    <Typography>{step.description}</Typography>
                    <Typography className="result-description">{step.result.description}</Typography>
                  </TimelineContent>
                </TimelineItem>
              );
            })}
          </Timeline>
        </Grid>
        <Grid item xs>
          <Card>
            <CardHeader title="Results" />
            <List>
              {results.map((result, index) => {
                return (
                  <ListItem key={index} className="result-list-item">
                    <ListItemIcon>
                      <CheckCircleOutlineTwoToneIcon color="success" />
                    </ListItemIcon>
                    <div>
                      <ListItemText primary={result.description} sx={{ mt: 0 }} />
                      {result.items.map((res, index) => getInfoItem(res, SuggestionType.info, index))}
                      {result.info.map((res, index) => {
                        return (
                          <Fragment key={index}>
                            {!res.hasError && getInfoItem(res.result, SuggestionType.info, index)}
                            {!res.hasError && res.suggestions?.length && (
                              <List>
                                <ListItem>
                                  <div>{res.suggestions?.map((suggestion, index) => getInfoItem(suggestion.suggestion, suggestion.type, index, suggestion.key))}</div>
                                </ListItem>
                              </List>
                            )}
                            {res.hasError && getInfoItem(res.errorMessage || '', SuggestionType.error, 0)}
                            {res.hasError && res.suggestions?.length && (
                              <List>
                                <ListItem>
                                  <div>{res.suggestions?.map((suggestion, index) => getInfoItem(suggestion.suggestion, suggestion.type, index, suggestion.key))}</div>
                                </ListItem>
                              </List>
                            )}
                          </Fragment>
                        );
                      })}
                    </div>
                  </ListItem>
                );
              })}
            </List>
          </Card>
        </Grid>
      </Grid>
    </Wrapper>
  );
};

export default ClientPage;
